import { inject, Injectable } from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { HisPersistenceService } from '../../core/service/backend/his-persistence.service';
import { HisRouteService } from '../../core/service/routing/his-route.service';
import { isBlank, isDefined, isNotDefined, keys, Optional, values } from '../../core/utils';
import { HisTableContextDirective } from '../directive/his-table-context.directive';
import { isGlobalTableConfigKey, isTableConfigKey, TABLE_CONFIG_ASPECTS, TABLE_CONFIG_KEYS, TableAspectDefinition, tableConfigAspect } from '../model/table-config-aspects';
import { defaultDevTableConfig, DevTableConfig, DevTableConfigChanges, DevTableConfigValues } from '../model/table-model';

function parseTableId( tableId: string | symbol ): string {
    return typeof tableId === 'symbol' ? '<anonymous>' : tableId;
}

@Injectable({
    providedIn: 'root'
})
export class HisTableService {
    private readonly persistence = inject( HisPersistenceService );
    private readonly routeService = inject( HisRouteService );

    private readonly BASE_CONFIG = Object.freeze<DevTableConfigValues>(defaultDevTableConfig());

    private readonly registeredDirectives = new Map<HisTableContextDirective, string | symbol>();
    private readonly registeredTables = new Map<string | symbol, BehaviorSubject<Readonly<DevTableConfig>>>();
    private readonly tableDefaults = new Map<string | symbol, DevTableConfigValues>();
    private readonly generatedTableIds = new Map<string, number>();

    /**
     * Registers a table context directive with the service, initializing its associated configuration if it wasn't before.
     * @param directive The table context directive to register.
     * @param defaults Optional default configuration values for the table.
     * @returns An observable of the table's configuration, which emits whenever the configuration changes.
     */
    public registerDirective( directive: HisTableContextDirective, defaults?: Partial<DevTableConfigValues> ): Observable<Readonly<DevTableConfig>> {
        const tableId = directive.getTableId();
        if ( !tableId ) {
            throw new Error( '[HisTableService] Cannot register directive without a valid table ID.' );
        }
        this.registeredDirectives.set( directive, tableId );
        if ( this.registeredTables.has( tableId ) ) {
            if ( defaults ) {
                console.debug( `[HisTableService] Table '${String(tableId)}' already initialized. Skipping re-initialization and ignoring new defaults.` );
            }
            return this.registeredTables.get( tableId )!.asObservable();
        }
        return this.initTableConfig( tableId, { defaults } );
    }

    /**
     * Unregisters a table context directive from the service, cleaning up its associated configuration if no other directives are using it.
     * @param directive The table context directive to unregister.
     */
    public unregisterDirective( directive: HisTableContextDirective ): void {
        const tableId = this.registeredDirectives.get( directive );
        if ( tableId ) {
            this.registeredDirectives.delete( directive );
            if ( ![...this.registeredDirectives.values()].includes( tableId ) ) {
                this.terminateConfig( tableId );
            }
        }
    }

    /**
     * Generates a unique and stable table ID based on the current route path and the provided suffix.\
     * If no suffix is provided and multiple identical IDs would be generated, a serial suffix is appended to subsequent IDs to ensure uniqueness.\
     * IDs are structured as `<routePath>($<suffix>)` OR `<routePath>(_<serial>)`
     * @example ```ts
     * const tableId  = this.generateTableId('myTable'); // 'cs.sys.hisng/some/route/:param$myTable'
     * const tableId2 = this.generateTableId('myTable'); // 'cs.sys.hisng/some/route/:param$myTable' (same as before)
     * const tableId3 = this.generateTableId();          // 'cs.sys.hisng/some/route/:param'
     * const tableId4 = this.generateTableId();          // 'cs.sys.hisng/some/route/:param_1'       (appended serial)
     * ```
     * **Note:** The serial generation is not a stable guarantee, as it is susceptible to changes in load order of tables on a page.
     * @param suffix The suffix to append to the route path. Relevant for distinguishing multiple tables on the same route.
     */
    public generateTableId( suffix?: string ): string {
        const module = this.routeService.getUrlElement( 'module' );
        const routePath = module + this.routeService.getRoutePathTemplate();
        if ( !isBlank( suffix ) ) {
            return `${routePath}$${suffix}`;
        }
        const count = this.generatedTableIds.get( routePath ) ?? 0;
        this.generatedTableIds.set( routePath, count + 1 );
        return count === 0 ? routePath : `${routePath}_${count}`;
    }

    /**
     * Initializes the default configuration for a table, merging any provided defaults with the base configuration.
     * @param defaults The default configuration values to merge with the base configuration.
     * @returns The merged default configuration.
     */
    public initDefaultConfig( defaults: Partial<DevTableConfigValues> = {} ): DevTableConfig {
        return {...this.mergeConfigs( defaults, this.BASE_CONFIG ), changes: null};
    }

    /**
     * Initializes the configuration for a table with the given ID, optionally persisting it and applying default values.
     * @param tableId The unique identifier for the table.
     * @param options Options for initializing the table configuration.
     * @returns An observable of the table's configuration, which emits whenever the configuration changes.
     */
    public initTableConfig( tableId: string | symbol, options: { persist?: boolean, defaults?: Partial<DevTableConfigValues> } = {} ): Observable<Readonly<DevTableConfig>> {
        const { persist = typeof tableId === 'string', defaults } = options;
        if ( this.registeredTables.has( tableId ) ) {
            if ( defaults ) {
                console.debug( `[HisTableService] Table '${parseTableId(tableId)}' already initialized. Skipping re-initialization and ignoring new defaults.` );
            } else {
                console.debug( `[HisTableService] Table '${parseTableId(tableId)}' already initialized. Skipping re-initialization.` );
            }
            return this.registeredTables.get( tableId )!.asObservable();
        }

        const initialConfig = this.initDefaultConfig( defaults ?? {} );
        this.tableDefaults.set( tableId, initialConfig );
        if ( persist && typeof tableId === 'symbol' ) {
            console.debug( '[HisTableService] Cannot read persistent configuration for anonymous table.' );
        }
        const effectiveConfig = this.applyUserConfig( tableId, persist, initialConfig );
        console.debug( `[HisTableService] Effective configuration for table '${parseTableId(tableId)}':`, effectiveConfig );
        this.registeredTables.set( tableId, new BehaviorSubject<Readonly<DevTableConfig>>( Object.freeze( {...effectiveConfig, changes: null} ) ) );

        return this.registeredTables.get( tableId )!.asObservable();
    }

    /**
     * Retrieves an observable of the table's configuration, which emits whenever the configuration changes.
     * @param tableId The unique identifier for the table.
     * @returns An observable of the table's configuration, or `undefined` if the table is not registered.
     */
    public getTableConfigObservable( tableId: string | symbol ): Observable<Readonly<DevTableConfig>> | undefined {
        const subject = this.getRegisteredTableConfig( tableId, 'getTableConfigObservable' );
        return subject?.asObservable();
    }

    /**
     * Retrieves the current value of the table's configuration.
     * @param tableId The unique identifier for the table.
     * @returns The current table configuration, or `undefined` if the table is not registered.
     */
    public getTableConfig( tableId: string | symbol ): Readonly<DevTableConfig> | undefined {
        return this.getRegisteredTableConfig( tableId, 'getTableConfig' )?.getValue();
    }

    /**
     * Updates the table's configuration with the provided patch.
     * @param tableId The unique identifier for the table.
     * @param patch Partial configuration to merge with the current configuration.
     * @param options Options for updating the table configuration.
     * @returns The updated table configuration, or `undefined` if the table is not registered.
     */
    public async patchTableConfig(
        tableId: string | symbol,
        patch: Partial<DevTableConfigValues>,
        options?: { persist?: boolean }
    ): Promise<Readonly<DevTableConfig> | undefined> {
        return this.updateConfig( tableId, patch, options?.persist ?? typeof tableId === 'string' );
    }

    /**
     * Sets a specific value in the table's configuration.
     * @param tableId The unique identifier for the table.
     * @param key The key of the configuration property to set.
     * @param value The value to set for the specified configuration property.
     * @param options Options for updating the table configuration.
     * @returns The updated table configuration, or `undefined` if the table is not registered.
     */
    public async setTableConfigValue<K extends keyof DevTableConfigValues>(
        tableId: string | symbol,
        key: K,
        value: DevTableConfigValues[K],
        options?: { persist?: boolean }
    ): Promise<Readonly<DevTableConfig> | undefined> {
        return this.updateConfig( tableId, { [key]: value }, options?.persist ?? typeof tableId === 'string' );
    }

    /**
     * Resets the table's configuration to its default values.
     * @param tableId The unique identifier for the table.
     * @param options Options for resetting the table configuration.
     * @returns The reset table configuration, or `undefined` if the table is not registered.
     */
    public async resetTableConfig( tableId: string | symbol, options?: { persist?: boolean } ): Promise<Readonly<DevTableConfig> | undefined> {
        const table = this.getRegisteredTableConfig( tableId, 'resetTableConfig' );
        if ( !table ) {
            return undefined;
        }

        const persist = options?.persist ?? typeof tableId === 'string';
        const onlyGlobalConf = !( persist && typeof tableId === 'string' );
        const tableIdStr = onlyGlobalConf ? 'global' : tableId;
        await Promise.allSettled( TABLE_CONFIG_KEYS.filter( key => !onlyGlobalConf || isGlobalTableConfigKey( key ) ).map( key => 
            this.persistence.deleteValue( tableConfigAspect( key ).persistenceKey( tableIdStr ) )
        ) );

        const defaults = this.tableDefaults.get( tableId ) ?? {};
        const resetConfig = this.mergeConfigs( defaults, this.BASE_CONFIG );
        this.updateConfigChanges( table, resetConfig );

        if ( this.hasGlobalChange( table.getValue().changes ) ) {
            this.updateAfterGlobalChange( table );
        }

        return table.getValue();
    }

    private async updateConfig(
        tableId: string | symbol,
        config: Partial<DevTableConfigValues>,
        persist: boolean = typeof tableId === 'string'
    ): Promise<Readonly<DevTableConfig> | undefined> {
        const table = this.getRegisteredTableConfig( tableId, 'patchTableConfig' );
        if ( !table ) {
            return undefined;
        }
        const persistOnlyGlobal = !( persist && typeof tableId === 'string' );
        const persistResult = this.persistConfig( tableId, persistOnlyGlobal, config );
        if (this.hasGlobalChange(config)) {
            await persistResult; //always await global changes
        }

        const currentConfig = table.getValue() as DevTableConfig;
        const mergedConfig = this.mergeConfigs( config, currentConfig );
        this.updateConfigChanges( table, mergedConfig );

        if ( this.hasGlobalChange( table.getValue().changes ) ) {
            this.updateAfterGlobalChange( table );
        }

        await persistResult;
        return table.getValue();
    }

    private hasGlobalChange( changes: DevTableConfigChanges | Partial<DevTableConfigValues> ): boolean {
        return !!changes && keys( changes ).some( key => isGlobalTableConfigKey( key ) );
    }

    private updateConfigChanges( table: BehaviorSubject<Readonly<DevTableConfig>>, newConfig: DevTableConfigValues ): void {
        const changesAcc: DevTableConfigChanges = {};
        const currentConfig = table.getValue() as DevTableConfig;
        for ( const key of keys( newConfig ) ) {
            if ( !isEqual( currentConfig[key], newConfig[key] ) ) {
                changesAcc[key] = { previous: currentConfig[key], current: newConfig[key] } as any;
            }
        }

        const changes = Object.keys( changesAcc ).length > 0 ? changesAcc : null;
        if ( changes ) {
            console.debug( '[HisTableService] Config changes:', changesAcc );
            table.next( Object.freeze( { ...newConfig, changes } ) );
        }
    }

    /** Updates all other registered configs after a global change has occurred */
    private updateAfterGlobalChange( originalTable: BehaviorSubject<Readonly<DevTableConfig>> ): void {
        [...this.registeredTables.entries()].forEach( ([tableId, table]) => {
            if ( table !== originalTable ) {
                this.updateConfigChanges( table, this.applyGlobalConfig( table.getValue() ) );
            }
        } );
    }

    private terminateConfig( tableId: string | symbol ): void {
        if ( this.registeredTables.has( tableId ) ) {
            this.registeredTables.get( tableId )!.complete();
            this.registeredTables.delete( tableId );
            this.tableDefaults.delete( tableId );
        }
    }

    /**
     * Retrieves the user-specific configuration for a table. (Including global values)
     * @param tableId The unique identifier for the table.
     * @returns The user-specific configuration values.
     */
    private getUserConfig( tableId: string ) {
        return this.doGetPersistentConfig( tableId, values( TABLE_CONFIG_ASPECTS ) );
    }

    /** Retrieves only the global configuration values. */
    private getGlobalConfig() {
        return this.doGetPersistentConfig( 'global', values( TABLE_CONFIG_ASPECTS ).filter( aspect => aspect.isGlobal ), true );
    }

    private doGetPersistentConfig( tableId: string, aspects: TableAspectDefinition<any>[], keepNull: boolean = false ): Partial<DevTableConfigValues> {
        return aspects.reduce( ( acc, aspect ) => {
            const value = this.persistence.readValue( aspect.persistenceKey( tableId ) );
            const deserializedValue = aspect.deserialize( value );
            if ( deserializedValue !== null || keepNull ) {
                ( acc as any )[aspect.configKey] = deserializedValue;
            }
            return acc;
        }, {} as Partial<DevTableConfigValues> );
    }

    /** Reads the user-specific configuration and applies it to the given table configuration. */
    private applyUserConfig( tableId: string | symbol, persist: boolean, config: DevTableConfigValues ): DevTableConfigValues {
        const onlyGlobalConf = !( persist && typeof tableId === 'string' );
        if ( onlyGlobalConf ) {
            return this.applyGlobalConfig( config );
        }

        const userConfig = this.getUserConfig( tableId );
        // user defined global default takes precedence over local developer default
        if ( isNotDefined( userConfig.tablePageSize ) && isDefined( userConfig.defaultTablePageSize ) ) {
            // by setting tablePageSize to null, we declare it as "defined null", so the custom dev conf doesnt overwrite it and the global default is used instead
            userConfig.tablePageSize = null;
        }
        return this.mergeConfigs( userConfig, config );
    }

    private applyGlobalConfig( config: DevTableConfigValues ): DevTableConfigValues {
        return this.mergeConfigs( this.getGlobalConfig(), config );
    }
    
    /** Merges two DevTableConfigValues objects, giving precedence to the values in configA. */
    private mergeConfigs( configA: Partial<DevTableConfigValues>, configB: DevTableConfigValues ): DevTableConfigValues {
        const firstDefined = <T>(a: Optional<T>, b: Optional<T>): T | undefined => isDefined(a) ? a : b;
        return {
            hiddenCols: configA.hiddenCols ?? [...configB.hiddenCols],
            frozenCol: configA.frozenCol ?? configB.frozenCol,
            colOrder: firstDefined(configA.colOrder, configB.colOrder),
            paginatorPosition: configA.paginatorPosition ?? configB.paginatorPosition,
            defaultTablePageSize: firstDefined(configA.defaultTablePageSize, configB.defaultTablePageSize),
            tablePageSize: firstDefined(configA.tablePageSize, configB.tablePageSize),
            columnsReorderable: configA.columnsReorderable ?? configB.columnsReorderable,
            showTableFilter: firstDefined(configA.showTableFilter, configB.showTableFilter)
        };
    }

    /**
     * Persists the table configuration values to the underlying storage.
     * @param tableId The unique identifier for the table.
     * @param config The configuration values to persist.
     * @returns A promise that resolves when all configuration values have been persisted.
     */
    private persistConfig( tableId: string | symbol, onlyGlobalConf: boolean, config: Partial<DevTableConfigValues> ) {
        const tableIdStr = typeof tableId === 'string' ? tableId : 'global';
        return Promise.allSettled( Object.keys( config )
            .filter( onlyGlobalConf ? isGlobalTableConfigKey : isTableConfigKey )
            .map( ( key ) => this.persistence.persistValue(
                tableConfigAspect( key ).persistenceKey( tableIdStr ),
                tableConfigAspect( key ).serialize( config[key] ) )
            )
        );
    }

    private getRegisteredTableConfig(
        tableId: string | symbol,
        operation: 'getTableConfig' | 'getTableConfigObservable' | 'patchTableConfig' | 'resetTableConfig'
    ): BehaviorSubject<Readonly<DevTableConfig>> | undefined {
        const table = this.registeredTables.get( tableId );
        if ( !table ) {
            console.warn( `[HisTableService] Cannot #${operation} for unknown table '${parseTableId(tableId)}'.` );
            return undefined;
        }
        return table;
    }
}